/**
 * 个人订阅管理器
 * @description 负责个人积分、套餐、权益列表、积分包、订阅历史
 * @author Joey
 */

import { useEffect, useRef, useState } from "react";
import { featureAll, FeatureAllData } from "@/services/feature-all";
import { showToastByCode } from '@/hooks/use-toast';
import { canUseWindow } from "@/utils/canuseWindow";
import { PaymentCallbackParams, Plan, subscriptionPlanHooks, subscriptionPlanManager, VIP_TYPE } from "./subscription-plan-manager";
import { pointGet } from "@/services/point-get";
import { featureEstimate, FeatureEstimateRequest } from "@/services/feature-estimate";
import { iamAccountHooks, iamAccountManager } from "./iam-account-manager";
import { useDecryptRouteData } from "@/hooks/use-decrypt-route-data";
import { OrderData, orderList } from "@/services/order-list";
import { featureConsumeAll, FeatureConsumeAllData } from "@/services/feature-consume-all";
import { featureDetailAll } from "@/services/feature-detail-all";
import { payInvoice } from "@/services/pay-invoice";
import { addYears } from "date-fns";
import { payCancelSubscriptionRoll } from "@/services/pay-cancel-subscription-roll";
import analytics from "@/utils/analytics";
import { useParams } from "react-router-dom";
import { featureEstimateAll, FeatureEstimateAllData } from "@/services/feature-estimate-all";
import { initCreditsMapping } from '@/components/agent-creation/constants/credits-mapping';
import { agentManager } from "./agent-manager";

/** 消耗积分事件 */
export const SPEND_CREDITS_EVENT = {
  /** AI 口播视频 */
  ai_oral_broadcast_video: "ai_oral_broadcast_video",
  /** 定制音频 */
  miuta_audio_clone_minimax: "miuta_audio_clone_minimax",
  /** 图片定制数字人 */
  image_number_person: "miuta_actor_fast_figure",
  /** 视频定制数字人（不消耗积分） */
  video_number_person: "miuta_actor_fast_figure",
} as const

type SpendCreditsEventValue = (typeof SPEND_CREDITS_EVENT)[keyof typeof SPEND_CREDITS_EVENT];

export interface AgentCreditsRecord {
  // 全部
  all: FeatureConsumeAllData[],
  // 消耗
  consumes: FeatureConsumeAllData[],
  // 入账
  increase: FeatureConsumeAllData[],
}

type FeaturesChangeEventData = { features: FeatureAllData[] };
type FeaturesChangeEvent = CustomEvent<FeaturesChangeEventData>;

type CreditsChangeEventData = { credits: number };
type CreditsChangeEvent = CustomEvent<CreditsChangeEventData>;

type AgentCreditsChangeEventData = { agentCredits: number };
type AgentCreditsChangeEvent = CustomEvent<AgentCreditsChangeEventData>;

type OrdersChangeEventData = { orders: OrderData[] };
type OrdersChangeEvent = CustomEvent<OrdersChangeEventData>;

type ConsumesChangeEventData = { consumes: FeatureConsumeAllData[] };
type ConsumesChangeEvent = CustomEvent<ConsumesChangeEventData>;

type AllsChangeEventData = { alls: FeatureConsumeAllData[] };
type AllsChangeEvent = CustomEvent<AllsChangeEventData>;

type PlanCreditsChangeEventData = { totalPlanCredits: number };
type PlanCreditsChangeEvent = CustomEvent<PlanCreditsChangeEventData>;

type TotalCreditsChangeEventData = { totalCredits: number };
type TotalCreditsChangeEvent = CustomEvent<TotalCreditsChangeEventData>;

type AgentCreditsRecordChangeEventData = { agentCreditsRecord: AgentCreditsRecord };
type AgentCreditsRecordChangeEvent = CustomEvent<AgentCreditsRecordChangeEventData>;

type FeatureEstimateAllChangeEventData = { featureEstimateAll: FeatureEstimateAllData[] };
type FeatureEstimateAllChangeEvent = CustomEvent<FeatureEstimateAllChangeEventData>;



class SubscriptionManager {
  private static instance: SubscriptionManager;
  private STORAGE_KEY = 'MIUTA_SUBSCRIPTION';
  // 积分
  private credits: number = null;
  // 权益列表
  private features: FeatureAllData[] = [];
  // 购买/订单历史
  private orders: OrderData[] = [];
  // 消费列表
  private consumes: FeatureConsumeAllData[] = [];
  // 全部列表（Consume+Buy）
  private alls: FeatureConsumeAllData[] = [];
  // 套餐积分剩余总数
  private totalPlanCredits: number = null;
  // 积分包剩余积分总数
  private totalCredits: number = null;

  private isInitialized = false;

  private agentId: string = null;
  // 智能体积分
  private agentCredits: number = null;
  // 智能体积分使用记录
  private agentCreditsRecord: AgentCreditsRecord = {
    // 全部
    all: [],
    // 消耗
    consumes: [],
    // 入账
    increase: [],
  }

  // 短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数
  private featureEstimateAll: FeatureEstimateAllData[] = [];

  private constructor() { }

  public static getInstance(): SubscriptionManager {
    if (!SubscriptionManager.instance) {
      SubscriptionManager.instance = new SubscriptionManager();
    }
    return SubscriptionManager.instance;
  }

  // 初始化权益列表，个人积分
  public async init() {
    try {
      await Promise.all([
        this.fetchFeatures(),
        // this.fetchCredits()
      ]);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      this.setIsInitialized(true);
    }
  }

  // 获取权益列表
  public async fetchFeatures(agentId?: string) {
    try {
      const features = await featureAll(agentId);
      this.setFeatures(features);
      return features
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取用户积分
  public async fetchCredits() {
    try {
      const credits = await pointGet();
      this.setCredits(credits);
      return credits
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数
  public async fetchFeatureEstimateAll() {
    try {
      const estimateAll = await featureEstimateAll({
        pid: 1002,
        featureCode: 'point',
      });
      this.setFeatureEstimateAll(estimateAll);

      return estimateAll
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取扣除预估
  public async getFeatureEstimate(params: FeatureEstimateRequest) {
    try {
      const estimate = await featureEstimate(params);
      return estimate
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取订单列表
  public async fetchOrders() {
    try {
      const orders = await orderList({
        // 1 个人、2团队（目前只看团队）
        findType: 1,
        page: 1,
        pageSize: 1000,
      });
      this.setOrders(orders.data);
      return orders
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取消费列表
  public async fetchConsumes(featureCode: string) {
    try {
      const consumesRes = await featureConsumeAll('', {
        featureCode,
        pid: 1001,
        page: 1,
        pageSize: 1000,
      });
      // 0 入账、1 消费
      const consumes = consumesRes.data.filter(item => item.type === 1)
      this.setConsumes(consumes);
      return consumes
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取智能体积分
  public async fetchAgentCredits(agentId?: string) {
    try {
      const credits = await pointGet(agentId || agentManager.getAgentId());
      this.setAgentCredits(credits);
      return credits
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取智能体消费列表
  public async fetchAgentConsumes(agentId?: string) {
    try {
      const consumesRes = await featureConsumeAll(agentId || this.getAgentId(), {
        featureCode: 'point',
        pid: 1002, // 智能体消费
        page: 1,
        pageSize: 1000,
      });

      // 取出pid为1002, featureCode为point 的数据
      const creditsConsumes = consumesRes.data.filter(item => item.pid === 1002 && item.featureCode === 'point')

      // 0/2 入账、1 消费
      const consumes = creditsConsumes.filter(item => item.type === 1)
      const increase = creditsConsumes.filter(item => [0, 2].includes(item.type))

      // 设置智能体积分使用记录
      this.setAgentCreditsRecord({
        all: creditsConsumes,
        consumes,
        increase,
      })
      return consumes
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取全部列表
  public async fetchAlls(featureCode: string) {
    try {
      const alls = await featureConsumeAll('', {
        featureCode,
        pid: 1001,
        page: 1,
        pageSize: 1000,
      });

      this.setAlls(alls.data);
      return alls
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取权益明细列表（计算套餐剩余积分和积分包剩余积分）
  public async fetchFeatureDetailAll() {
    try {
      const featureDetails = await featureDetailAll();

      // 定义套餐积分对应的item_code列表
      const planItemCodes = [
        'item_miuta_starter',
        'item_miuta_pro',
        'item_miuta_premium'
      ];

      // 初始化积分计数器
      let totalPlanCredits = 0;
      let totalCredits = 0;

      featureDetails
        .filter(item => item.featureCode === 'point') // 筛选积分权益
        .forEach(pointItem => {
          // 计算剩余积分（total_val - used_val）
          const remaining = pointItem.totalVal - pointItem.usedVal;
          // 获取item_code
          const itemCode = pointItem.orderItem.itemInfo.itemCode;

          // 判断积分类型并累加
          if (planItemCodes.includes(itemCode)) {
            totalPlanCredits += remaining;
          } else {
            totalCredits += remaining;
          }
        });

      this.setTotalPlanCredits(totalPlanCredits);
      this.setTotalCredits(totalCredits);
      return featureDetails
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 获取stripe发票
  public async fetchPayInvoice(orderNo: string) {
    try {
      const invoice = await payInvoice(orderNo);
      return invoice
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  // 撤销取消订阅-只限订阅周期到期之前
  public async cancelSubscriptionRoll(subscriptionId: string) {
    try {
      const res = await payCancelSubscriptionRoll(subscriptionId);
      return res
    } catch (error) {
      console.log(error);
      throw error;
    }
  }


  // ===================== 属性读取写入 =====================
  public getCredits() {
    return this.credits;
  }
  public setCredits(credits: number) {
    this.credits = credits;
    this.dispatchCreditsChange(credits);
  }

  // 智能体ID
  public getAgentId() {
    return this.agentId;
  }
  public setAgentId(agentId: string) {
    this.agentId = agentId;
  }

  // 智能体积分
  public getAgentCredits() {
    return this.agentCredits;
  }
  public setAgentCredits(agentCredits: number) {
    this.agentCredits = agentCredits;
    this.dispatchAgentCreditsChange(agentCredits);
  }

  public getFeatures() {
    return this.features;
  }
  public setFeatures(features: FeatureAllData[]) {
    this.features = features;
    this.dispatchFeaturesChange(features);
  }

  public getIsInitialized() {
    return this.isInitialized
  }
  public setIsInitialized(isInitialized: boolean) {
    this.isInitialized = isInitialized;
  }

  public getOrders() {
    return this.orders;
  }
  public setOrders(orders: OrderData[]) {
    this.orders = orders;
    this.dispatchOrdersChange(orders);
  }

  public getConsumes() {
    return this.consumes;
  }
  public setConsumes(consumes: FeatureConsumeAllData[]) {
    this.consumes = consumes;
    this.dispatchConsumesChange(consumes);
  }

  public getAlls() {
    return this.alls;
  }
  public setAlls(alls: FeatureConsumeAllData[]) {
    this.alls = alls;
    this.dispatchAllsChange(alls);
  }

  // 套餐积分剩余总数
  public getTotalPlanCredits() {
    return this.totalPlanCredits;
  }
  public setTotalPlanCredits(totalPlanCredits: number) {
    this.totalPlanCredits = totalPlanCredits;
    this.dispatchPlanCreditsChange(totalPlanCredits);
  }

  // 积分包剩余积分总数
  public getTotalCredits() {
    return this.totalCredits;
  }
  public setTotalCredits(totalCredits: number) {
    this.totalCredits = totalCredits;
    this.dispatchTotalCreditsChange(totalCredits);
  }

  // 智能体积分使用记录
  public getAgentCreditsRecord() {
    return this.agentCreditsRecord;
  }
  public setAgentCreditsRecord(agentCreditsRecord: AgentCreditsRecord) {
    this.agentCreditsRecord = agentCreditsRecord;
    this.dispatchAgentCreditsRecordChange(agentCreditsRecord);
  }

  // 短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数
  public getFeatureEstimateAll() {
    return this.featureEstimateAll;
  }
  public setFeatureEstimateAll(featureEstimateAll: FeatureEstimateAllData[]) {
    this.featureEstimateAll = featureEstimateAll;
    this.dispatchFeatureEstimateAllChange(featureEstimateAll);
  }

  // ===================== 派发事件 =====================
  // 监听权益列表变化
  public onFeaturesChange(listener: (e: FeaturesChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:features`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:features`, listener);
      };
    }
  }
  private dispatchFeaturesChange(features: FeatureAllData[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<FeaturesChangeEventData>(
        `${this.STORAGE_KEY}:features`,
        { detail: { features } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听积分变化
  public onCreditsChange(listener: (e: CreditsChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:credits`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:credits`, listener);
      };
    }
  }
  private dispatchCreditsChange(credits: number) {
    if (canUseWindow()) {
      const event = new CustomEvent<CreditsChangeEventData>(
        `${this.STORAGE_KEY}:credits`,
        { detail: { credits } }
      );
      window.dispatchEvent(event);
    }
  }
  // 监听智能体积分变化
  public onAgentCreditsChange(listener: (e: AgentCreditsChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:agentCredits`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:agentCredits`, listener);
      };
    }
  }
  private dispatchAgentCreditsChange(agentCredits: number) {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentCreditsChangeEventData>(
        `${this.STORAGE_KEY}:agentCredits`,
        { detail: { agentCredits } }
      );
      window.dispatchEvent(event);
    }
  }


  // 监听订单变化
  public onOrdersChange(listener: (e: OrdersChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:orders`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:orders`, listener);
      };
    }
  }
  private dispatchOrdersChange(orders: OrderData[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<OrdersChangeEventData>(
        `${this.STORAGE_KEY}:orders`,
        { detail: { orders } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听消费列表变化
  public onConsumesChange(listener: (e: ConsumesChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:consumes`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:consumes`, listener);
      };
    }
  }
  private dispatchConsumesChange(consumes: FeatureConsumeAllData[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<ConsumesChangeEventData>(
        `${this.STORAGE_KEY}:consumes`,
        { detail: { consumes } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听 All 列表变化
  public onAllsChange(listener: (e: AllsChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:alls`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:alls`, listener);
      };
    }
  }
  private dispatchAllsChange(alls: FeatureConsumeAllData[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<AllsChangeEventData>(
        `${this.STORAGE_KEY}:alls`,
        { detail: { alls } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听套餐积分变化
  public onPlanCreditsChange(listener: (e: PlanCreditsChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:planCredits`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:planCredits`, listener);
      };
    }
  }
  private dispatchPlanCreditsChange(planCredits: number) {
    if (canUseWindow()) {
      const event = new CustomEvent<PlanCreditsChangeEventData>(
        `${this.STORAGE_KEY}:planCredits`,
        { detail: { totalPlanCredits: planCredits } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听积分包积分变化
  public onTotalCreditsChange(listener: (e: TotalCreditsChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:totalCredits`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:totalCredits`, listener);
      };
    }
  }
  private dispatchTotalCreditsChange(totalCredits: number) {
    if (canUseWindow()) {
      const event = new CustomEvent<TotalCreditsChangeEventData>(
        `${this.STORAGE_KEY}:totalCredits`,
        { detail: { totalCredits } }
      );
      window.dispatchEvent(event);
    }
  }
  // 监听智能体积分使用记录变化
  public onAgentCreditsRecordChange(listener: (e: AgentCreditsRecordChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:agentCreditsRecord`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:agentCreditsRecord`, listener);
      };
    }
  }
  private dispatchAgentCreditsRecordChange(agentCreditsRecord: AgentCreditsRecord) {
    if (canUseWindow()) {
      const event = new CustomEvent<AgentCreditsRecordChangeEventData>(
        `${this.STORAGE_KEY}:agentCreditsRecord`,
        { detail: { agentCreditsRecord } }
      );
      window.dispatchEvent(event);
    }
  }

  // 监听短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数变化
  public onFeatureEstimateAllChange(listener: (e: FeatureEstimateAllChangeEvent) => void) {
    if (canUseWindow()) {
      window.addEventListener(`${this.STORAGE_KEY}:featureEstimateAll`, listener);

      return () => {
        window.removeEventListener(`${this.STORAGE_KEY}:featureEstimateAll`, listener);
      };
    }
  }
  private dispatchFeatureEstimateAllChange(featureEstimateAll: FeatureEstimateAllData[]) {
    if (canUseWindow()) {
      const event = new CustomEvent<FeatureEstimateAllChangeEventData>(
        `${this.STORAGE_KEY}:featureEstimateAll`,
        { detail: { featureEstimateAll } }
      );
      window.dispatchEvent(event);
    }
  }
}

// ===================== Hook =====================
// 权益列表
export const useSubscription = () => {
  const [loading, setLoading] = useState(false)
  const [features, setFeatures] = useState<FeatureAllData[]>(subscriptionManager.getFeatures())
  const [plans, setPlans] = useState<Plan[]>()
  const { account } = iamAccountHooks.useData();

  const init = async () => {
    setLoading(true);
    try {
      // 没有初始化时，才调用初始化接口
      const [features, plans] = await Promise.all([
        subscriptionManager.fetchFeatures(),
        subscriptionPlanManager.fetchPlans(),
      ]);
      setPlans(plans);
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onFeaturesChange((e) => {
      setFeatures(e.detail.features)
    })
  }, [])

  const member = () => {
    // pid = 1001 && feature_code = 'miuta_starter'  代表有基础包会员
    // pid = 1001 && feature_code = 'miuta_pro'  代表有升级包会员
    // pid = 1001 && feature_code = 'miuta_premium'  代表有高级包会员
    const currentFeature = features?.find(item => item.pid === 1001 && item.featureCode === account?.nowVipType);

    // 根据 currentFeature 的 featureCode 来查找定价列表中的对应套餐
    const currentPlan = plans?.find(plan => plan.featureCode === currentFeature?.featureCode)
    return {
      ...currentFeature,
      plan: currentPlan,
    }
  }

  return {
    features,
    plans,
    member: member(),
    loading,
  }
}

// 获取权益列表
export const useGetFeature = () => {
  const [features, setFeatures] = useState<FeatureAllData[]>(subscriptionManager.getFeatures())

  useEffect(() => {
    return subscriptionManager.onFeaturesChange((e) => {
      setFeatures(e.detail.features)
    })
  }, [])

  return {
    features,
  }
}

// 单独对权益列表发起请求，获取权益列表数据
export const useFetchFeatureData = () => {
  const params = useParams();
  const [features, setFeatures] = useState<FeatureAllData[]>(subscriptionManager.getFeatures())
  const [loading, setLoading] = useState(false)

  const init = async (agentId?: string) => {
    setLoading(true);
    try {
      // 没有初始化时，才调用初始化接口
      await subscriptionManager.fetchFeatures(agentId || agentManager.getAgentId())
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onFeaturesChange((e) => {
      setFeatures(e.detail.features)
    })
  }, [])

  return {
    features,
    loading,
  }
}

// 获取个人积分
export const useCredits = () => {
  const [credits, setCredits] = useState<number>(subscriptionManager.getCredits())
  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      // 积分为null，才调用初始化接口
      // if (subscriptionManager.getCredits() === null) {
      //   await subscriptionManager.fetchCredits();
      // }

      // 模拟1秒的loading
      await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onCreditsChange((e) => {
      setCredits(e.detail.credits)
    })
  }, [])

  return {
    credits,
    loading,
  }
}

// 获取智能体积分
export const useAgentCredits = () => {
  const [agentCredits, setAgentCredits] = useState<number>(subscriptionManager.getAgentCredits())
  const [loading, setLoading] = useState(false)

  const fetchAgentCredits = async (agentId: string) => {
    setLoading(true);
    try {
      await subscriptionManager.fetchAgentCredits(agentId);
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (agentManager.getAgentId()) {
      fetchAgentCredits(agentManager.getAgentId());
    }
  }, [])

  useEffect(() => {
    return subscriptionManager.onAgentCreditsChange((e) => {
      setAgentCredits(e.detail.agentCredits)
    })
  }, [])

  return {
    agentCredits,
    loading,
    fetchAgentCredits,
  }
}

// 获取智能体消费列表
export const useAgentConsumes = () => {
  const params = useParams();
  const [loading, setLoading] = useState(false)
  const [agentCreditsRecord, setAgentCreditsRecord] = useState(subscriptionManager.getAgentCreditsRecord())

  const fetchAgentConsumes = async () => {
    setLoading(true);
    try {
      await subscriptionManager.fetchAgentConsumes(agentManager.getAgentId());
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    subscriptionManager.setAgentId(agentManager.getAgentId());
    fetchAgentConsumes();
  }, [])

  useEffect(() => {
    return subscriptionManager.onAgentCreditsRecordChange((e) => {
      setAgentCreditsRecord(e.detail.agentCreditsRecord)
    })
  }, [])


  return {
    loading,
    agentCreditsRecord,
    fetchAgentConsumes,
  }
}

// 获取扣除预估
export const useFeatureEstimate = () => {
  const [spendCredits, setSpendCredits] = useState<number>(0)
  const debounceRef = useRef<number | null>(null);

  const debounceEstimate = async ({ event, count }: { event: SpendCreditsEventValue, count: number }) => {
    // 清除之前的防抖定时器
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(async () => {
      setSpendCredits(await getEstimate({ event, count }));
    }, 300);
  };

  const getEstimate = async ({ event, count }: { event: SpendCreditsEventValue, count: number }) => {
    const data = {
      event,
      count,
      pid: 1002, // 默认
      featureCode: 'point', // 默认
    }
    try {
      const estimate = await subscriptionManager.getFeatureEstimate(data);
      return estimate;
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    }
  }

  return {
    spendCredits,
    debounceEstimate,
  }
}

// 处理支付成功或失败的回调
export const usePaymentStatusModal = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showFailModal, setShowFailModal] = useState(false)
  const [buyPlan, setBuyPlan] = useState<FeatureAllData>()
  const [expireTime, setExpireTime] = useState<number>(null)
  const oldKey = subscriptionPlanManager.getCreateSubscriptionKey()

  const { create, loading: creating } = subscriptionPlanHooks.useCreateSubscription()
  const { polling, startPolling } = subscriptionPlanHooks.usePollFeature()

  // 获取路由参数
  const { data } = useDecryptRouteData<PaymentCallbackParams>('k');

  const handlePayStatus = async () => {
    // 没有订阅key
    if (!oldKey) return
    // 路由参数没有plan
    if (!data?.sku) return

    // 支付成功
    if (oldKey === data?.success) {
      console.log('pay success...')
      try {
        // 购买积分，无需轮询
        if (data?.type !== 'credits') {
          console.log('支付成功，轮询请求权益列表，检查是否包含指定套餐的权益...')
          const res = await startPolling(data.sku)
          console.log('套餐到账！')
          setBuyPlan(res)
          setExpireTime(res.expireTime)
          setShowSuccessModal(true)
          return;
        }
        // 积分一年后过期
        const oneYearLater = addYears(new Date(), 1)
        setExpireTime(oneYearLater.getTime() / 1000)
        setShowSuccessModal(true)

        // 支付成功埋点
        analytics.trackPaymentSuccess({
          user_email: iamAccountManager.get()?.email,
          plan_id: data.sku,
          currency: 'usd',
          payment_method: 'stripe',
        })
      } catch (error) {
        console.log(error)
        setShowFailModal(true)
        // 支付失败埋点
        analytics.trackPaymentFailed({
          user_email: iamAccountManager.get()?.email,
          plan_id: data.sku,
          currency: 'usd',
          payment_method: 'stripe',
        })
      }
    }
    // 支付失败
    else if (oldKey === data?.fail) {
      setShowFailModal(true)
    }
  }

  useEffect(() => {
    handlePayStatus()
  }, [location.pathname])

  const onSuccessModalOpenChange = (open: boolean) => {
    setShowSuccessModal(open)
  }

  const onFailModalOpenChange = (open: boolean) => {
    setShowFailModal(open)
    subscriptionPlanManager.clearCreateSubscriptionKey()
    window.location.href = location.origin + location.pathname
  }

  // 失败重试
  const onRetry = async () => {
    if (data?.sku) {
      const params = { sku: data.sku, type: data.type, cancelSub: undefined }
      // 升级或降级需要传入当前订阅id
      if (data?.type === 'plan' && iamAccountManager.get()?.nowVipTypeSubscriptionId) {
        params.cancelSub = iamAccountManager.get()?.nowVipTypeSubscriptionId
      }
      await create(params)
    }
    setShowFailModal(false)
  }

  return {
    showSuccessModal,
    showFailModal,
    onSuccessModalOpenChange,
    onFailModalOpenChange,
    onRetry,
    creating,
    polling,
    buyPlan,
    expireTime,
  }
}

// 获取订单列表
export const useOrders = () => {
  const [orders, setOrders] = useState<OrderData[]>(subscriptionManager.getOrders())
  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      await subscriptionManager.fetchOrders();
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onOrdersChange((e) => {
      setOrders(e.detail.orders)
    })
  }, [])

  return {
    orders,
    loading,
  }
}

// 获取消耗列表
export const useConsumes = () => {
  const [consumes, setConsumes] = useState<FeatureConsumeAllData[]>(subscriptionManager.getConsumes())
  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      await subscriptionManager.fetchConsumes('point');
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onConsumesChange((e) => {
      setConsumes(e.detail.consumes)
    })
  }, [])

  return {
    consumes,
    loading,
  }
}

// 获取 All 列表
export const useAlls = () => {
  const [alls, setAlls] = useState(subscriptionManager.getAlls())
  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      await subscriptionManager.fetchAlls('point');
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onAllsChange((e) => {
      setAlls(e.detail.alls)
    })
  }, [])

  return {
    alls,
    loading,
  }
}

// 获取套餐剩余积分和积分包积分
export const useRemainCredits = () => {
  const [totalPlanCredits, setTotalPlanCredits] = useState(subscriptionManager.getTotalPlanCredits())
  const [totalCredits, setTotalCredits] = useState(subscriptionManager.getTotalCredits())

  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      await subscriptionManager.fetchFeatureDetailAll();
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onPlanCreditsChange((e) => {
      setTotalPlanCredits(e.detail.totalPlanCredits)
    })
  }, [])

  useEffect(() => {
    return subscriptionManager.onTotalCreditsChange((e) => {
      setTotalCredits(e.detail.totalCredits)
    })
  }, [])

  return {
    totalPlanCredits,
    totalCredits,
    loading,
  }
}

// 获取 stripe 发票
export const usePayInvoice = () => {
  const [loading, setLoading] = useState(false)

  const downloadVoice = async (orderNo: string) => {
    setLoading(true);
    try {
      const data = await subscriptionManager.fetchPayInvoice(orderNo);
      // data.pdfUrl
      window.open(data.hostedUrl, '_blank')
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    downloadVoice,
    loading,
  }
}

// 撤销取消订阅-只限订阅周期到期之前
export const useReactivation = () => {
  const [loading, setLoading] = useState(false)

  const reactivation = async (subscriptionId: string) => {
    setLoading(true);
    try {
      await subscriptionManager.cancelSubscriptionRoll(subscriptionId);
      // 刷新页面
      window.location.reload();
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  return {
    reactivation,
    loading,
  }
}

// 检查会员状态、积分是否足够（废弃）
export const useCheckVipAndCredits = () => {
  const [openFeatureExpiredModal, setOpenFeatureExpiredModal] = useState(false);
  const [openCreditsEmptyModal, setOpenCreditsEmptyModal] = useState(false);

  const { account } = iamAccountHooks.useData();
  const credits = subscriptionManager.getCredits();

  const check = () => {

    // 白名单不校验（暂时）
    if (whiteList.includes(account?.email)) {
      console.log('白名单不校验')
      return true
    }

    // 套餐过期
    if (account?.vipType === VIP_TYPE.expired) {
      setOpenFeatureExpiredModal(true);
      return false
    }

    // 积分不足
    if (credits <= 0) {
      setOpenCreditsEmptyModal(true);
      return false
    }

    return true
  }

  return {
    check,
    openFeatureExpiredModal,
    openCreditsEmptyModal,
    handleModalClose: () => {
      setOpenFeatureExpiredModal(false);
      setOpenCreditsEmptyModal(false);
    }
  }
}

// 获取短视频、Ai创作、图文、文章、播客（包含口播模式）、朋友圈的消耗积分数
export const useFeatureEstimateAll = () => {
  const [featureEstimateAll, setFeatureEstimateAll] = useState(subscriptionManager.getFeatureEstimateAll())
  const [loading, setLoading] = useState(false)

  const init = async () => {
    setLoading(true);
    try {
      const data = await subscriptionManager.fetchFeatureEstimateAll();

      // 初始化积分映射表
      initCreditsMapping(data);
    } catch (error) {
      console.log(error);
      showToastByCode(error)
      throw error;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    init();
  }, [])

  useEffect(() => {
    return subscriptionManager.onFeatureEstimateAllChange((e) => {
      setFeatureEstimateAll(e.detail.featureEstimateAll)
    })
  }, [])

  return {
    loading,
    featureEstimateAll,
  }
}

// 检查积分是否足够
export const useCheckCredits = () => {
  // 兜底：当智能体积分没值时（如 CreditConsumeButton 在未请求过智能体积分的页面被使用），主动拉取一次
  useEffect(() => {
    if (subscriptionManager.getAgentCredits() !== null) return;
    const agentId = agentManager.getAgentId();
    if (!agentId) return;

    subscriptionManager.fetchAgentCredits(agentId);
  }, [])

  const hasEnoughCredits = (credits: number) => {
    const agentCredits = subscriptionManager.getAgentCredits()
    console.log('Remain credits', { agentCredits, credits })

    return agentCredits >= credits
  }

  return {
    hasEnoughCredits,
  }
}

export const subscriptionHook = {
  useSubscription,
  useCredits,
  useGetFeature,
  useFeatureEstimate,
  usePaymentStatusModal,
  useOrders,
  useConsumes,
  useAlls,
  useRemainCredits,
  usePayInvoice,
  useReactivation,
  useCheckVipAndCredits,
  useCheckCredits,
  // 智能体积分
  useAgentCredits,
  useAgentConsumes,
  useFetchFeatureData,
  useFeatureEstimateAll,
}


export const subscriptionManager = SubscriptionManager.getInstance();

// 白名单
const whiteList = [
  "x760058573@gmail.com",
  "xioda2023@gmail.com",
  "1677308711@qq.com",
  "lixingcan89@gmail.com",
  "yezhang051@gmail.com",
  "chunfanfan1001@gmail.com",
  "1759579235hxk@gmail.com",
  "a994601404@gmail.com",
  "1759579235chatme@gmail.com",
  // "junlang@sjzn.com",
  "jiayanwuinfo@gmail.com",
  "xnan3899@gmail.com",
  "nanzengliu@gmail.com",
  "614916565@qq.com",
  "1906429666@qq.com",
  "999@qq.com",
  "aa@qq.com",
  "779207025@qq.com",
  "aaaa@qq.com",
  "aaaaa@qq.com",
  "aaaaaa@qq.com",
  "aasasa@qq.com",
  "ss@qq.com",
  "aaasas@qq.com",
  "asasasa@qq.com",
  "aaaaaaa@qq.co",
  "you@example.com",
  "zhsdmqbjzxsc@gmail.com",
  "99mvvideo@gmail.com",
  "2715314318@qq.com",
  "qiaoyi@sjzn.com",
  "alice.yo.ung2835@gmail.com",
  "wm400359@gmail.com",
  "hanzl2110@gmail.com",
  "andylee2014@gmail.com",
  "xieyinhu2005@gmail.com",
  "avami0717@gmail.com",
  "xdaochi@163.com",
  "1367824220@qq.com",
  "mengmengmengqiang@gmail.com",
  "zengxisheng@yeah.net",
  "vera20250407@gmail.com",
  "cherie.song1@gmail.com",
  "chenhd70@gmail.com",
  "lyyf2002@gmail.com",
  // "zjunbin1286@gmail.com",
  "jake_so511@hotmail.com",
  "13123@qq.com",
  "supa090603@gmail.com",
  // "1669940775@qq.com",
  "qq738613780@gmail.com",
  "cc239065@gmail.com",
  "zhihaoli594@gmail.com",
  "xmz969123@gmail.com",
  "2332473532@qq.com",
  "809737487@qq.com",
  "huajie16@qq.com",
  "448778089@qq.com",
  "xiaobaotalent@gmail.com",
  "349849271@qq.com",
  "tutipielkage12485@gmail.com",
  "724316747@qq.com",
  "wgs6@qq.com",
  "lshypf@gmail.com",
  "1163935708@qq.com",
  "hyh59985998@gmail.com",
  "951768221@qq.com",
  "harryailene47@gmail.com",
  "568483846@qq.com",
  "1990mlbmlb@gmail.com",
  "13890268522@163.com",
  "one6day.day@gmail.com",
  "704464322@qq.com",
  "fadsfasdf@qq.com",
  "bh0600350@gmail.com",
  "erick.mshard@gmail.com",
  "khail@sjzn.com",
  "yiguzhaomu@gmail.com",
  "123123@qq.com",
  "yalunlu613@gmail.com",
  "842165540@qq.com",
  "13076286286@163.com",
  "12345@qq.com",
  "111222333@qq.com",
  "1234@qq.com",
  "1020153123@qq.com",
  "110356264@qq.com",
  "1670350745@qq.com"
]